import { type ReactNode } from 'react'

import { type ProductionOrderSummaryType } from '@aeris/contracts'
import { Badge, type StatusTone } from '@aeris/ui'
import {
  orderSectors,
  type SectorProgress,
  type UniformSector,
} from '@aeris/uniforms'

import { ART_STATUS_META, STAGE_META } from '../lib/production-stage-meta.tsx'

/**
 * Os quatro selos do pedido — onde cada setor está (F171).
 *
 * Atendimento, Arte, Produção, Expedição: é o "status de cada um" que o dono
 * da 4Play pediu, lido de uma olhada no cartão e no topo da ficha. O estado vem
 * de `orderSectors` (`@aeris/uniforms`), derivado do que já está gravado.
 */

type SectorOrder = Pick<
  ProductionOrderSummaryType,
  'gradeChecked' | 'artStatus' | 'stage' | 'status'
>

const TONES: Record<SectorProgress, StatusTone> = {
  pendente: 'neutral',
  andamento: 'warning',
  feito: 'success',
}

const SECTOR_NAMES: Record<UniformSector, string> = {
  atendimento: 'Atendimento',
  arte: 'Arte',
  producao: 'Produção',
  expedicao: 'Expedição',
}

/** A palavra de cada selo: o que o setor está fazendo agora. */
const sectorWord = (
  sector: UniformSector,
  progress: SectorProgress,
  order: SectorOrder
): string => {
  switch (sector) {
    case 'atendimento':
      if (progress === 'andamento') return 'com o cliente'
      return progress === 'feito' ? 'grade conferida' : 'grade pendente'
    case 'arte':
      return ART_STATUS_META[order.artStatus].label.toLowerCase()
    case 'producao':
      if (progress === 'feito') return 'pronto'
      return progress === 'andamento'
        ? STAGE_META[order.stage].label.toLowerCase()
        : 'esperando a arte'
    case 'expedicao':
      if (progress === 'feito') return 'saiu'
      return progress === 'andamento' ? 'pronto para sair' : '—'
  }
}

export const ProductionSectors = ({
  order,
  compact = false,
}: {
  order: SectorOrder
  /** No cartão do quadro: só os setores que não terminaram. */
  compact?: boolean
}): ReactNode => {
  const sectors = orderSectors({
    gradeChecked: order.gradeChecked,
    artStatus: order.artStatus,
    stage: order.stage,
    fulfilled: order.status === 'fulfilled',
  })

  const shown = (Object.keys(SECTOR_NAMES) as UniformSector[]).filter(
    (sector) => !compact || sectors[sector] !== 'feito'
  )

  return (
    <ul className="flex flex-wrap gap-1" aria-label="Andamento por setor">
      {shown.map((sector) => (
        <li key={sector}>
          <Badge tone={TONES[sectors[sector]]}>
            {SECTOR_NAMES[sector]}: {sectorWord(sector, sectors[sector], order)}
          </Badge>
        </li>
      ))}
    </ul>
  )
}
